// src/components/Costplanner/AdvancedCalculator.jsx
import { useState, useMemo, useRef, useEffect, useContext, useCallback } from "react";
import { useMaterials } from "../../contexts/MaterialContext";
import { UtilitiesContext } from "../../contexts/UtilitiesContext";
import { StaffContext } from "../../contexts/StaffContext";
import { useFeedback } from "../../contexts/FeedbackContext";
import { useMarketing } from "../../contexts/MarketingContext";
import { usePackaging } from "../../contexts/PackagingContext";
import { usePlanner } from "../../contexts/PlannerContext";
import { useStorage } from "../../contexts/StorageContext";
import { usePricingSettings } from "../../contexts/PricingSettingsContext";
// import UploadOrCamera from "@/components/Controls/UploadOrCamera"; // (unused)
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import * as THREE from "three";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Bounds, Center } from "@react-three/drei";
import logo from "../../assets/mudo-logo-bg.png";
import { compressImage } from "../../utils/imageCompression";
import ParametricBuilder from "../Design/ParametricBuilder";
import { estimateManufacture } from "../../ai/estimators/manufactureEstimator";
import { updateWithActuals } from "../../ai/learning/timeLearner";
import { PricingCoachPanel } from "./PricingCoachPanel";
import { ScheduleCoachPanel } from "./ScheduleCoachPanel";
import { estimateProductionSchedule } from "../../ai/estimators/productionScheduleEstimator";
import CalculatorHelperPanel from "./CalculatorHelperPanel";

/* ---------- presets & helpers ---------- */
const PRODUCTS_FILE = "products.json";

const shapePresets = {
  Mug: { sections: [60, 60, 60, 60] },
  Plate: { sections: [100, 100, 100, 100] },
  Vase: { sections: [40, 50, 60, 40] },
  Bowl: { sections: [80, 80, 40, 5] },
  Planter: { sections: [80, 90, 90, 80] },
};
const defaultCategories = Object.keys(shapePresets);

// ---- helper: rectangular visual (stacked square sections) ----
function RectangularShape({ segments }) {
  // Precompute box positions/sizes (mm). We display at 1/100 scale like the lathe.
  const boxes = useMemo(() => {
    let y = 0;
    const out = [];
    for (const s of segments) {
      const h = Math.max(0, Number(s.height) || 0);
      const d = Math.max(0, Number(s.radius) * 2 || 0); // width = depth = diameter
      if (h > 0 && d > 0) {
        out.push({ y: y + h / 2, d, h });
      }
      y += h;
    }
    return out;
  }, [segments]);

  return (
    <group scale={[0.01, 0.01, 0.01]}>
      {boxes.map((b, i) => (
        <mesh key={i} position={[0, b.y, 0]}>
          <boxGeometry args={[b.d, b.h, b.d]} />
          <meshStandardMaterial color="#d0c4ae" side={THREE.DoubleSide} />
        </mesh>
      ))}
    </group>
  );
}

const defaultClays = [
  { name: "Earthenware", density: 1.85 },
  { name: "Mid Fire", density: 1.9 },
  { name: "Stoneware", density: 2.0 },
  { name: "Grogged", density: 1.88 },
  { name: "Porcelain", density: 2.2 },
  { name: "Paperclay", density: 1.75 },
  { name: "Slipcasting", density: 1.85 },
  { name: "Feeneys", density: 1.92 },
  { name: "Clayworks", density: 1.95 },
  { name: "Laguna", density: 2.05 },
];

const defaultMethodThickness = { Dip: 0.05, Brush: 0.1, Spray: 0.02 };

/**
 * Estimate kiln space units per piece (rough heuristic based on size).
 * @param {{ height?: number, topDiameter?: number, bottomDiameter?: number }} dimensions
 */
function estimateSpaceUnitsPerPiece(dimensions) {
  if (!dimensions) return 1;

  const safeHeight = Number(dimensions.height) || 0;
  const safeTop = Number(dimensions.topDiameter) || Number(dimensions.bottomDiameter) || 0;

  if (safeHeight <= 0 || safeTop <= 0) return 1;

  const footprint = safeTop * safeTop;
  const rawUnits = (footprint * safeHeight) / 5000;
  const clamped = Math.min(5, Math.max(0.5, rawUnits));
  return Number(clamped.toFixed(2));
}

/**
 * Suggest retail/wholesale targets from true unit cost.
 */
function buildPricingCoach(unitCost, options = {}) {
  if (!unitCost || unitCost <= 0) {
    return {
      ok: false,
      reason: "noCost",
      unitCost: 0,
      retailPrice: null,
      wholesalePrice: null,
      effectiveHourly: null,
      notes: ["Add materials, staff, kiln and packaging costs to see pricing suggestions."],
    };
  }

  const {
    targetRetailMargin = 0.65,
    targetWholesaleMargin = 0.5,
    totalLabourHours = null,
    batchSize = 1,
  } = options;

  const retailPrice = unitCost / (1 - targetRetailMargin);
  const wholesalePrice = unitCost / (1 - targetWholesaleMargin);

  const notes = [];
  if (unitCost < 0.01) {
    notes.push("Your cost per piece is extremely low – double-check that all materials and labour are included.");
  }
  if (unitCost > 1000) {
    notes.push("Your cost per piece is very high – check quantities and batch size are correct.");
  }
  if (retailPrice < unitCost) {
    notes.push("Your current target retail price is below true cost. Increase pricing or reduce time/costs.");
  }

  notes.push(
    `To hit ~${Math.round(targetRetailMargin * 100)}% margin, retail should be around ${retailPrice.toFixed(
      2
    )} per piece.`
  );
  notes.push(
    `For ~${Math.round(targetWholesaleMargin * 100)}% margin, wholesale should be around ${wholesalePrice.toFixed(
      2
    )} per piece.`
  );

  return {
    ok: true,
    reason: null,
    unitCost,
    retailPrice,
    wholesalePrice,
    effectiveHourly: null,
    targetRetailMargin,
    targetWholesaleMargin,
    notes,
  };
}

function integrateLathe(points, samples = 300) {
  const totalH = points[points.length - 1].y;
  const dy = totalH / samples;
  let V = 0, S = 0;
  for (let i = 0; i <= samples; i++) {
    const y = i * dy;
    let idx = points.findIndex((p, j) => y <= (points[j + 1]?.y || Infinity));
    if (idx < 0) idx = points.length - 2;
    const a = points[idx], b = points[idx + 1];
    const t = (y - a.y) / ((b.y - a.y) || 1);
    const r = a.r + t * (b.r - a.r);
    const drdy = (b.r - a.r) / ((b.y - a.y) || 1);
    const w = i === 0 || i === samples ? 0.5 : 1;
    V += w * Math.PI * r * r;
    S += w * 2 * Math.PI * r * Math.sqrt(1 + drdy * drdy);
  }
  return { volume: V * dy, surface: S * dy };
}

function SectionalShape({ profile }) {
  const sideGeo = useMemo(() => {
    const pts = [new THREE.Vector2(profile[0].r, 0), ...profile.map((p) => new THREE.Vector2(p.r, p.y))];
    return new THREE.LatheGeometry(pts, 64);
  }, [profile]);

  const capGeo = useMemo(() => {
    const g = new THREE.CircleGeometry(profile[0].r, 64);
    g.rotateX(-Math.PI / 2);
    return g;
  }, [profile]);

  const topGeo = useMemo(() => {
    const last = profile[profile.length - 1];
    const g = new THREE.CircleGeometry(last.r, 64);
    g.rotateX(Math.PI / 2);
    g.translate(0, last.y, 0);
    return g;
  }, [profile]);

  return (
    <group>
      <mesh geometry={sideGeo}>
        <meshStandardMaterial color="#d0c4ae" side={THREE.DoubleSide} />
      </mesh>
      <mesh geometry={capGeo}>
        <meshStandardMaterial color="#d0c4ae" side={THREE.DoubleSide} />
      </mesh>
      <mesh geometry={topGeo}>
        <meshStandardMaterial color="#d0c4ae" side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

function RectShell({ segments, wallThk, baseThk }) {
  // renders outer boxes and hollow inner boxes (above baseThk)
  const group = useRef();
  return (
    <group ref={group}>
      {segments.reduce(
        (acc, s, i) => {
          const yStart = acc.y;
          const yEnd = yStart + s.height;
          const centerY = (yStart + yEnd) / 2;
          const d = s.radius * 2;

          // outer box
          acc.nodes.push(
            <mesh key={`o-${i}`} position={[0, centerY, 0]}>
              <boxGeometry args={[d, s.height, d]} />
              <meshStandardMaterial color="#d0c4ae" opacity={0.8} transparent />
            </mesh>
          );

          // inner (hollow) above baseThk
          const hollowStart = Math.max(yStart, baseThk);
          const hollowH = Math.max(0, yEnd - hollowStart);
          const innerD = Math.max(0, d - 2 * wallThk);
          if (innerD > 0 && hollowH > 0) {
            const innerCenterY = (hollowStart + yEnd) / 2;
            acc.nodes.push(
              <mesh key={`i-${i}`} position={[0, innerCenterY, 0]}>
                <boxGeometry args={[innerD, hollowH, innerD]} />
                <meshStandardMaterial color="#9aa39b" opacity={0.5} transparent wireframe />
              </mesh>
            );
          }

          acc.y = yEnd;
          return acc;
        },
        { y: 0, nodes: [] }
      ).nodes}
    </group>
  );
}

/* ---------- Planner task helper ---------- */
function startOfDay(dt = new Date()) {
  const d = new Date(dt);
  d.setHours(9, 0, 0, 0);
  return d;
}
function addHours(dt, h) {
  const d = new Date(dt);
  d.setHours(d.getHours() + h);
  return d;
}
function addDays(dt, d) {
  const x = new Date(dt);
  x.setDate(x.getDate() + d);
  return x;
}
function iso(dt) {
  try { return new Date(dt).toISOString(); } catch { return new Date().toISOString(); }
}

function stageColor(k) {
  switch (k) {
    case "forming":        return "#06b6d4";
    case "drying":         return "#22c55e";
    case "bisque":         return "#f59e0b";
    case "bisqueCooldown": return "#facc15";
    case "glazing":        return "#a78bfa";
    case "glazeFiring":    return "#ef4444";
    case "glazeCooldown":  return "#fb7185";
    case "qcPack":         return "#14b8a6";
    case "dispatch":       return "#64748b";
    default:               return "#f97316";
  }
}

function makePlannerTasksFromProduct(product) {
  const p = product?.process || {};
  const qty = Number(product?.quantity || 1);
  const start = p.startDate ? new Date(p.startDate) : new Date();

  // core durations
  const dryingDays     = Number(p.dryingDays ?? 2);
  const bufferHours    = Number(p.bufferHours ?? 6);
  const bisqueHours    = Number(p.bisqueHours ?? 14);
  const glazeHours     = Number(p.glazeHours ?? 12);

  // NEW: cool-downs
  const bisqueCooldown = Number(p.bisqueCooldownHours ?? 24);
  const glazeCooldown  = Number(p.glazeCooldownHours  ?? 24);

  // timeline
  const formingStart       = startOfDay(start);
  const formingEnd         = addHours(formingStart, Math.max(1, bufferHours));

  const dryingStart        = formingEnd;
  const dryingEnd          = startOfDay(addDays(start, Math.max(1, dryingDays)));

  const bisqueStart        = dryingEnd;
  const bisqueEnd          = addHours(bisqueStart, Math.max(1, bisqueHours));

  const bisqueCoolStart    = bisqueEnd;
  const bisqueCoolEnd      = addHours(bisqueCoolStart, Math.max(0, bisqueCooldown));

  const glazingStart       = bisqueCoolEnd;
  const glazingEnd         = addHours(glazingStart, Math.max(1, bufferHours));

  const glazeFiringStart   = glazingEnd;
  const glazeFiringEnd     = addHours(glazeFiringStart, Math.max(1, glazeHours));

  const glazeCoolStart     = glazeFiringEnd;
  const glazeCoolEnd       = addHours(glazeCoolStart, Math.max(0, glazeCooldown));

  const qcPackStart        = glazeCoolEnd;
  const qcPackEnd          = addHours(startOfDay(addDays(qcPackStart, 1)), 17);

  const dispatchStart      = startOfDay(addDays(qcPackStart, 1));
  const dispatchEnd        = addHours(dispatchStart, 17);

  const stages = [
    { key: "forming",        label: "Forming",            start: formingStart,     end: formingEnd },
    { key: "drying",         label: "Drying",             start: dryingStart,      end: dryingEnd },
    { key: "bisque",         label: "Bisque Firing",      start: bisqueStart,      end: bisqueEnd },
    { key: "bisqueCooldown", label: "Bisque Cool Down",   start: bisqueCoolStart,  end: bisqueCoolEnd },
    { key: "glazing",        label: "Glazing",            start: glazingStart,     end: glazingEnd },
    { key: "glazeFiring",    label: "Glaze Firing",       start: glazeFiringStart, end: glazeFiringEnd },
    { key: "glazeCooldown",  label: "Glaze Cool Down",    start: glazeCoolStart,   end: glazeCoolEnd },
    { key: "qcPack",         label: "QC & Pack",          start: qcPackStart,      end: qcPackEnd },
    { key: "dispatch",       label: "Dispatch",           start: dispatchStart,    end: dispatchEnd },
  ];

  let prevId = null;
  const toId = () =>
    (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  return stages.map((stg) => {
    const id = toId();
    const task = {
      id,
      title: `${product?.name || "Product"} — ${stg.label}`,
      start: iso(stg.start),
      end: iso(stg.end),
      qty,
      batch: product?.process?.batchId || product?.batchName || product?.id || "Batch",
      category: product?.category || "Batch",
      color: stageColor(stg.key),
      tags: [stg.key],
      status: "todo",
      progress: 0,
      predecessorType: "FS",
      dependencies: prevId ? [String(prevId)] : [],
      notes: `Auto-planned from Calculator for “${product?.name || "Unnamed"}”.`,
    };
    prevId = id;
    return task;
  });
}
/* ------------------- component ------------------- */
export default function AdvancedCalculator({
  product,
  settings,
  updateProduct,
  onSaveProduct,
  onBack,
}) {
  /* contexts */
  const { materials, scrapedMaterials } = useMaterials();
  const { utilities } = useContext(UtilitiesContext);
  const { staffList } = useContext(StaffContext);
  const { play, buzz } = useFeedback();
  const { monthlyTotal: marketingMonthlyTotal = 0 } = useMarketing() || {};
  const { resolvePackagingUsage, items: packagingLibrary = [] } = usePackaging();
  const planner = (() => { try { return usePlanner(); } catch { return null; } })();
  const { hasFolder, loadJSON, saveJSON } = useStorage();
  const { pricingSettings } = usePricingSettings();

  const safeMaterials = Array.isArray(materials) ? materials : [];
  const safeScrapedMaterials = Array.isArray(scrapedMaterials) ? scrapedMaterials : [];
  const allMaterials = safeScrapedMaterials.length > 0 ? safeScrapedMaterials : safeMaterials;

  /* helpers */
  const handleField = (field, value) => updateProduct({ ...product, [field]: value });

  /* ---------- PHOTO: drag & drop + click + camera + paste (with compression) ---------- */
  const MAX_PRODUCT_IMAGE_BYTES = 2 * 1024 * 1024; // 2 MB target

  const [photoError, setPhotoError] = useState("");
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const dropRef = useRef(null);

  const allowedTypes = [
    "image/png",
    "image/jpeg",
    "image/webp",
    "image/svg+xml",
    "image/heic",
    "image/heif",
  ];

  async function handleProductImage(file) {
    if (!file) return;
    setPhotoError("");

    if (!allowedTypes.includes(file.type)) {
      setPhotoError("Please upload PNG, JPG/JPEG, WEBP, HEIC/HEIF, or SVG.");
      play("error"); buzz("error");
      return;
    }

    try {
      const { dataURL } = await compressImage(file, {
        maxBytes: MAX_PRODUCT_IMAGE_BYTES,
        maxWidth: 2000,
        maxHeight: 2000,
        preferredMime: "image/webp",
      });

      updateProduct({ ...product, imageUrl: dataURL });
      play("success"); buzz("light");
    } catch (e) {
      console.error("[Product image] compression failed:", e);
      setPhotoError("Could not process that image.");
      play("error"); buzz("error");
    }
  }

  function onFileChange(e) {
    const f = e.target.files?.[0];
    if (f) handleProductImage(f);
    e.target.value = ""; // allow re-selecting same file
  }
  function pickFile()   { fileInputRef.current?.click(); }
  function pickCamera() { cameraInputRef.current?.click(); }
  function removePhoto(){ updateProduct({ ...product, imageUrl: "" }); play("stop"); buzz("light"); }

  const prevent = (e) => { e.preventDefault(); e.stopPropagation(); };

  useEffect(() => {
    const zone = dropRef.current;
    if (!zone) return;

    const onOver  = (e) => { prevent(e); setIsDragging(true); };
    const onEnter = (e) => { prevent(e); setIsDragging(true); };
    const onLeave = (e) => { prevent(e); if (!zone.contains(e.relatedTarget)) setIsDragging(false); };
    const onDrop  = (e) => {
      prevent(e); setIsDragging(false);
      const f = e.dataTransfer?.files?.[0];
      if (f) handleProductImage(f);
    };
    const onPaste = (e) => {
      const items = e.clipboardData?.items || [];
      for (const it of items) {
        if (it.type?.startsWith("image/")) {
          const f = it.getAsFile();
          if (f) { handleProductImage(f); break; }
        }
      }
    };

    zone.addEventListener("dragover", onOver);
    zone.addEventListener("dragenter", onEnter);
    zone.addEventListener("dragleave", onLeave);
    zone.addEventListener("drop", onDrop);
    zone.addEventListener("paste", onPaste);

    return () => {
      zone.removeEventListener("dragover", onOver);
      zone.removeEventListener("dragenter", onEnter);
      zone.removeEventListener("dragleave", onLeave);
      zone.removeEventListener("drop", onDrop);
      zone.removeEventListener("paste", onPaste);
    };
  }, []);

  /* ---------- glaze, utilities, staff cost etc ---------- */
  const customGlaze = useMemo(
    () => allMaterials.filter((m) => (m.category || "").toLowerCase() === "glaze"),
    [allMaterials]
  );
  const glazeOptions = useMemo(() => customGlaze.map((m) => m.name), [customGlaze]);

  const selectedMat = useMemo(
    () => allMaterials.find((m) => String(m.id) === String(product?.materialId)) || null,
    [allMaterials, product?.materialId]
  );
  const materialUnitCost = useMemo(() => {
    if (!selectedMat) return 0;
    const cost = parseFloat(selectedMat.purchaseCost) || 0;
    const conv = parseFloat(selectedMat.conversionAmount) || 1;
    return conv > 0 ? cost / conv : 0;
  }, [selectedMat]);

  const monthlyOutput = settings?.productionOutput || 1;
  const utilityCost = useMemo(() => {
    return (utilities || []).reduce((sum, u) => {
      let monthly = 0;
      switch (u.frequency) {
        case "Weekly":
          monthly = u.cost * 4.333; break;
        case "Fortnightly":
          monthly = u.cost * 2.166; break;
        case "Monthly":
          monthly = u.cost; break;
        case "Quarterly":
          monthly = u.cost / 3; break;
        case "Yearly":
          monthly = u.cost / 12; break;
        default:
          monthly = Number(u.cost) || 0;
      }
      return sum + monthly / monthlyOutput;
    }, 0);
  }, [utilities, monthlyOutput]);

  /* ---------- 3D shape controls ---------- */
  const shapeMode = product.shapeMode || "Lathe"; // "Lathe" | "Rectangular"

  const [category, setCategory] = useState(product.category || defaultCategories[0]);
  const preset = shapePresets[category];
  const initialSegments = product.segments?.length
    ? product.segments
    : preset.sections.map((r) => ({ radius: r / 2, height: 50 }));
  const [segments, setSegments] = useState(initialSegments);
  const [wallThk, setWallThickness] = useState(
    product.wallThickness !== undefined ? product.wallThickness : 2
  );
  const [baseThk, setBaseThickness] = useState(
    product.baseThickness !== undefined ? product.baseThickness : 2
  );
  const [manufactureEstimate, setManufactureEstimate] = useState(null);

  const totalHeight = useMemo(
    () => segments.reduce((a, s) => a + s.height, 0),
    [segments]
  );

  // profile (for lathe)
  const profile = useMemo(() => {
    let cum = 0;
    return segments.map((s) => ({ y: (cum += s.height), r: s.radius }));
  }, [segments]);

  /** @returns {ProductDraft | null} */
  function buildDraftFromForm() {
    const radii = Array.isArray(segments)
      ? segments.map((s) => Number(s?.radius) || 0)
      : [];
    const topRadius = radii.length ? radii[radii.length - 1] : 0;
    const bottomRadius = radii.length ? radii[0] : 0;
    const maxRadius = radii.reduce((m, r) => Math.max(m, r), 0);
    const diameterMmTop = Number((topRadius || maxRadius) * 2);
    const diameterMmBottom = Number((bottomRadius || maxRadius) * 2);
    const heightMm = Number(totalHeight || 0);

    if (!heightMm || !diameterMmTop) return null;

    const normalizedType = ["Mug", "Planter", "Bowl", "Plate", "Vase", "Custom"].includes(
      category
    )
      ? category
      : "Custom";

    const batchSize = product.batchSize || product.quantity || 1;
    const dimensions = {
      height: heightMm,
      topDiameter: diameterMmTop,
      bottomDiameter: diameterMmBottom,
      shape: "Cylinder",
    };
    const spaceUnitsPerPiece = estimateSpaceUnitsPerPiece(dimensions);
    const totalSpaceUnits = (Number(batchSize) || 0) * spaceUnitsPerPiece;

    return {
      name: product.name || "",
      type: normalizedType,
      shape: "Cylinder",
      dimensions: {
        heightMm,
        diameterMmTop,
        diameterMmBottom,
      },
      batchSize,
      spaceUnitsPerPiece,
      totalSpaceUnits,
    };
  }

  useEffect(() => {
    const draft = buildDraftFromForm();
    if (!draft) {
      setManufactureEstimate(null);
      return;
    }
    const method = product.method || "Wheel Throwing";
    const hasHandle = !!product.hasHandle;
    const layers = Array.isArray(product.glazing) ? Math.max(1, product.glazing.length) : 1;
    try {
      const est = estimateManufacture(draft, method, hasHandle, layers);
      setManufactureEstimate(est);
    } catch (e) {
      console.error("[AdvancedCalculator] estimateManufacture failed", e);
      setManufactureEstimate(null);
    }
  }, [
    category,
    segments,
    totalHeight,
    product.method,
    product.glazing,
    product.batchSize,
    product.quantity,
    product.name,
    product.hasHandle,
  ]);

  /* ---------- geometry metrics per shape ---------- */
  function clampPos(n) { return Number.isFinite(n) ? Math.max(0, n) : 0; }

  // Rectangular metrics (square plan: width = depth = diameter)
  function rectangularMetrics(segments, wall, base) {
    let y = 0;
    let outerVol = 0;
    let innerVol = 0;
    let outerSideArea = 0;
    let innerSideArea = 0;
    let topOuterArea = 0;
    let baseInnerFaceArea = 0;

    segments.forEach((s, i) => {
      const h = clampPos(s.height);
      const d = clampPos(s.radius * 2);  // outer width/depth (mm)
      const perim = 4 * d;                // square perimeter

      // outer contributions
      outerVol += d * d * h;              // mm^3
      outerSideArea += perim * h;         // mm^2
      if (i === segments.length - 1) {
        topOuterArea = d * d;             // mm^2 (open top face)
      }

      // inner (hollow) contributions — only above base thickness
      const segYStart = y;
      const segYEnd = y + h;
      const hollowStart = Math.max(segYStart, base); // interior starts above base
      const hollowHeight = clampPos(segYEnd - hollowStart);
      const innerD = clampPos(d - 2 * wall);

      if (innerD > 0 && hollowHeight > 0) {
        innerVol += innerD * innerD * hollowHeight;     // mm^3
        innerSideArea += 4 * innerD * hollowHeight;     // mm^2
        if (segYStart < base && segYEnd >= base) {
          // interior base face at y = base
          baseInnerFaceArea = innerD * innerD;          // mm^2
        }
      }

      y += h;
    });

    const outerArea = outerSideArea + topOuterArea;   // mm^2 (no bottom)
    const innerArea = innerSideArea + baseInnerFaceArea; // mm^2

    return { outerVol, innerVol, outerArea, innerArea };
  }

  // Lathe metrics via existing integrateLathe
  const latheMetrics = useMemo(() => {
    const outer = integrateLathe([{ y: 0, r: profile[0].r }, ...profile], 300);

    // inner profile: account for base thickness and wall thickness
    const innerProfile = (() => {
      const adjusted = [];
      let accumulated = 0;
      for (let i = 0; i < segments.length; i++) {
        const seg = segments[i];
        const yStart = accumulated;
        const yEnd = accumulated + seg.height;
        accumulated = yEnd;

        if (yEnd <= baseThk) {
          adjusted.push({ y: yEnd, r: 0 });
        } else if (yStart < baseThk && yEnd > baseThk) {
          const t = (baseThk - yStart) / seg.height;
          const rAtBase = seg.radius * t;
          adjusted.push({ y: baseThk, r: 0 });
          adjusted.push({ y: baseThk, r: Math.max(0, rAtBase - wallThk) });
          adjusted.push({ y: yEnd, r: Math.max(0, seg.radius - wallThk) });
        } else if (yStart >= baseThk) {
          adjusted.push({ y: yEnd, r: Math.max(0, seg.radius - wallThk) });
        }
      }
      return adjusted;
    })();

    const inner = integrateLathe([{ y: 0, r: 0 }, ...innerProfile], 300);

    // interior base face (circle) at baseThk
    const baseRadius = (() => {
      for (let i = 1; i < profile.length; i++) {
        const a = profile[i - 1];
        const b = profile[i];
        if (a.y <= baseThk && b.y >= baseThk) {
          const t = (baseThk - a.y) / ((b.y - a.y) || 1);
          const rInterp = a.r + t * (b.r - a.r);
          return Math.max(0, rInterp - wallThk);
        }
      }
      return 0;
    })();

    const baseFaceArea = Math.PI * baseRadius * baseRadius;

    return {
      outerVol: outer.volume,                 // mm^3
      innerVol: inner.volume,                 // mm^3
      outerArea: outer.surface,               // mm^2 (side surface incl. rim)
      innerArea: inner.surface + baseFaceArea // mm^2 (sides + interior base)
    };
  }, [profile, segments, wallThk, baseThk]);

  // choose metrics by shape
  const { outerVol, innerVol, outerArea, innerArea } = useMemo(() => {
    if (shapeMode === "Rectangular") {
      return rectangularMetrics(segments, wallThk, baseThk);
    }
    // default: Lathe
    return latheMetrics;
  }, [shapeMode, segments, wallThk, baseThk, latheMetrics]);

  // final shared figures
  const shellVolCm3 = Math.max(0, outerVol - innerVol) / 1000; // mm^3 -> cm^3
  const outerAreaCm2 = outerArea / 100;                         // mm^2 -> cm^2
  const innerAreaCm2 = innerArea / 100;                         // mm^2 -> cm^2

  // Parametric metrics state (must be before glazeCost calculation)
  const [parametricMetrics, setParametricMetrics] = useState(null);
  const handleParametricMetrics = useCallback((metrics) => {
    setParametricMetrics(metrics);
  }, []);

  // Use parametric metrics if available, otherwise use old shape metrics
  const activeShellVolCm3 = parametricMetrics?.volume_cm3 ?? shellVolCm3;
  const activeOuterAreaCm2 = parametricMetrics?.outerSurfaceArea_cm2 ?? outerAreaCm2;
  const activeInnerAreaCm2 = parametricMetrics?.innerSurfaceArea_cm2 ?? innerAreaCm2;

  /* ---------- costs that depend on areas/volumes ---------- */
  const glazeCost = useMemo(() => {
    if (!product.glazing || !Array.isArray(product.glazing)) return 0;
    return product.glazing.reduce((sum, layer) => {
      const mat = customGlaze.find((m) => m.name === layer.type);
      const uc = mat
        ? (parseFloat(mat.purchaseCost) || 0) / (parseFloat(mat.conversionAmount) || 1)
        : 0;
      const area =
        layer.scope === "Internal"
          ? activeInnerAreaCm2
          : layer.scope === "External"
          ? activeOuterAreaCm2
          : activeInnerAreaCm2 + activeOuterAreaCm2;
      const thickCm = (Number(layer.thickness) || 0) / 10; // mm → cm
      const coats = Math.max(1, Number(layer.coats) || 1);
      const coverage = Number(layer.coverage ?? 100) / 100;
      const vol = area * thickCm * coats * coverage;
      return sum + vol * uc;
    }, 0);
  }, [product.glazing, activeInnerAreaCm2, activeOuterAreaCm2, customGlaze]);

  const staffCost = useMemo(() => {
    if (!product.time) return 0;
    const batchSize = product.batchSize || 1;
    const quantity = product.quantity || 1;

    return Object.entries(product.time).reduce((sum, [, stage]) => {
      const type = stage.chargeType || "In-house";
      let stageTotal = 0;

      if (type === "Skip" || type === "Non-billable") return sum;
      if (type === "Outsourced") stageTotal = stage.manualCost || 0;
      if (type === "In-house") {
        const assigns = stage.staffAssignments || [];
        stageTotal = assigns.reduce((acc, { minutes = 0, rate = 0 }) => {
          const durationHours = (minutes || 0) / 60;
          return acc + durationHours * rate;
        }, 0);
      }

      const perItemCost = stageTotal / batchSize;
      return sum + perItemCost * quantity;
    }, 0);
  }, [product.time, product.batchSize, product.quantity]);

  const [clayType, setClayType] = useState(product.clayType || defaultClays[0].name);
  const clay = defaultClays.find((c) => c.name === clayType);
  const clayWeight = useMemo(
    () => (activeShellVolCm3 * (clay?.density || 1)).toFixed(1),
    [activeShellVolCm3, clay]
  );
  const materialCost = useMemo(
    () => parseFloat(clayWeight || 0) * materialUnitCost,
    [clayWeight, materialUnitCost]
  );

  /* ===== Packaging: Library (manual) + Rules (context) ===== */
  const packagingMats = useMemo(() => {
    if (!Array.isArray(packagingLibrary)) return [];
    return packagingLibrary.map((item) => {
      const wasteMult = 1 + (Number(item?.wastePct || 0) / 100);
      let unitBase = 0;
      if (item?.pricingMode === "unit" && item.unitPrice != null) {
        unitBase = Number(item.unitPrice);
      } else if (
        item?.pricingMode === "pack" &&
        item.packPrice != null &&
        item.unitsPerPack
      ) {
        const units = Math.max(1, Number(item.unitsPerPack) || 1);
        unitBase = Number(item.packPrice) / units;
      }

      const purchaseCost = unitBase * wasteMult;
      const conversionAmount = Math.max(1, Number(item?.yieldPerUnit || 1));
      return {
        ...item,
        purchaseCost,
        conversionAmount,
      };
    });
  }, [packagingLibrary]);
  const packagingUnitCost = (mat) => {
    if (!mat) return 0;
    const cost = parseFloat(mat.purchaseCost) || 0;
    const conv = parseFloat(mat.conversionAmount) || 1;
    return conv > 0 ? cost / conv : 0;
  };

  const packagingItemsCost = useMemo(() => {
    const items = product.packaging?.items || [];
    return items.reduce((sum, it) => {
      const mat = packagingMats.find((m) => String(m.id) === String(it.materialId));
      return sum + packagingUnitCost(mat) * (Number(it.qty) || 0);
    }, 0);
  }, [product.packaging?.items, packagingMats]);

  const ruleUsage = useMemo(() => {
    try {
      return resolvePackagingUsage({
        productId: product?.id ?? null,
        batchId: product?.process?.batchId ?? null,
        quantity: Number(product?.quantity || 1),
      });
    } catch {
      return { perUnitCost: 0, perBatchCost: 0, totalCost: 0, breakdown: [] };
    }
  }, [resolvePackagingUsage, product?.id, product?.process?.batchId, product?.quantity]);

  const packagingCost = useMemo(() => {
    const legacy = Number(product?.packaging?.cost) || 0;
    const shipping = Number(product?.packaging?.shipping) || 0;
    return packagingItemsCost + ruleUsage.totalCost + legacy + shipping;
  }, [packagingItemsCost, ruleUsage.totalCost, product?.packaging?.cost, product?.packaging?.shipping]);

  /* ===== Other extras ===== */
  const accessoriesCost = useMemo(() => {
    return (product.accessories || []).reduce(
      (sum, a) => sum + (Number(a.cost) || 0) * (Number(a.qty) || 0),
      0
    );
  }, [product.accessories]);

  /* ===== Overheads ===== */
  const [useMarketingCtx, setUseMarketingCtx] = useState(
    product?.overheads?.useMarketingCtx ?? true
  );

  const salaryMonthly = Number(product?.overheads?.salary) || 0;
  const marketingMonthly = useMarketingCtx
    ? Number(marketingMonthlyTotal || 0)
    : Number(product?.overheads?.marketing || 0);
  const outputMonthly =
    Number(product?.overheads?.output) ||
    Number(settings?.productionOutput || 0) ||
    0;

  const overheadCost = useMemo(() => {
    const totalMonthly = salaryMonthly + marketingMonthly;
    return outputMonthly > 0 ? totalMonthly / outputMonthly : 0;
  }, [salaryMonthly, marketingMonthly, outputMonthly]);

  const totalCost =
    materialCost +
    glazeCost +
    staffCost +
    utilityCost +
    accessoriesCost +
    packagingCost +
    overheadCost;

  const quantityForUnitCost = Number(product?.quantity || 1) || 1;
  const unitCost = quantityForUnitCost > 0 ? totalCost / quantityForUnitCost : totalCost;

  const formatMoney = useCallback((num) => Number(num || 0).toFixed(2), []);

  const chartData = [
    { name: "Material", value: Number(materialCost) || 0 },
    { name: "Glaze", value: Number(glazeCost) || 0 },
    { name: "Staff", value: Number(staffCost) || 0 },
    { name: "Utilities", value: Number(utilityCost) || 0 },
    { name: "Accessories", value: Number(accessoriesCost) || 0 },
    { name: "Packaging", value: Number(packagingCost) || 0 },
    { name: "Overheads", value: Number(overheadCost) || 0 },
  ];
  const COLORS = ["#f97316", "#eab308", "#3b82f6", "#10b981", "#a855f7", "#f43f5e", "#22d3ee"];

  const pricingCoach = useMemo(
    () =>
      buildPricingCoach(unitCost, {
        targetRetailMargin: pricingSettings?.targetRetailMargin ?? 0.65,
        targetWholesaleMargin: pricingSettings?.targetWholesaleMargin ?? 0.5,
        totalLabourHours: manufactureEstimate?.totalMinutes ? manufactureEstimate.totalMinutes / 60 : null,
        batchSize: product?.batchSize || product?.quantity || 1,
      }),
    [unitCost, manufactureEstimate?.totalMinutes, product?.batchSize, product?.quantity, pricingSettings]
  );

  const scheduleCoach = useMemo(() => {
    const draft = buildDraftFromForm();
    if (!draft) return null;
    const options = {
      startDate: new Date(),
      dueDate: product?.dueDate || null,
      totalHours: manufactureEstimate?.totalMinutes ? manufactureEstimate.totalMinutes / 60 : null,
    };
    // TODO: When PlannerContext integration is ready, push this into a proposed plan for one-click add.
    return estimateProductionSchedule(draft, options);
  }, [product?.dueDate, manufactureEstimate?.totalMinutes, segments, category, totalHeight, product?.batchSize, product?.quantity, product?.name, product?.hasHandle]);
  const batchLabel = product?.name || product?.title || product?.productName || "Batch";
  const batchId = product?.id || product?.batchId || null;

  const exportRef = useRef();
  const exportPDF = () => {
    if (!exportRef.current) return;
    html2canvas(exportRef.current).then((canvas) => {
      const pdf = new jsPDF("p", "mm", "a4");
      const img = canvas.toDataURL("image/png");
      const w = pdf.internal.pageSize.getWidth();
      const h = (canvas.height * w) / canvas.width;
      pdf.addImage(img, "PNG", 0, 0, w, h);
      pdf.save("calculator-summary.pdf");
      play("success");
      buzz("light");
    });
  };
  const exportExcel = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([
      ["Category", "Cost"],
      ["Material", materialCost],
      ["Glaze", glazeCost],
      ["Staff", staffCost],
      ["Utilities", utilityCost],
      ["Accessories", accessoriesCost],
      ["Packaging", packagingCost],
      ["Overheads", overheadCost],
      ["Total", totalCost],
    ]);
    XLSX.utils.book_append_sheet(wb, ws, "Summary");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf], { type: "application/octet-stream" }), "calculator-summary.xlsx");
    play("success");
    buzz("light");
  };

  // 3D
  const maxRadius = useMemo(() => Math.max(...segments.map((s) => s.radius)), [segments]);
  const scale = useMemo(() => 2 / Math.max(totalHeight, maxRadius * 2, 1), [totalHeight, maxRadius]);

  // FitView refs (must live INSIDE the component)
  const controlsRef = useRef(null);
  const boundsRef = useRef(null);

  // Auto-fit whenever geometry/mode changes
  useEffect(() => {
    requestAnimationFrame(() => {
      try {
        boundsRef.current?.refresh().fit();
      } catch {}
    });
  }, [segments, shapeMode, totalHeight]);
  const addSeg = () => {
    setSegments((s) => [...s, { ...s[s.length - 1] }]);
    play("tap");
  };
  const removeSeg = () => {
    setSegments((s) => (s.length > 2 ? s.slice(0, -1) : s));
    play("tap");
  };

  // ---------- NEW: Process → Planner controls (inline panel) ----------
  const process = product?.process || {};
  const setProcess = (patch) =>
    updateProduct({ ...product, process: { ...(product.process || {}), ...patch } });

  // ---------- Mirror product to folder (workspace) ----------
  async function saveDraftToWorkspace(payload) {
    if (!hasFolder) return false;
    try {
      const disk = await loadJSON(PRODUCTS_FILE, []);
      const arr = Array.isArray(disk) ? disk : [];
      const idx = arr.findIndex((p) => String(p.id) === String(payload.id));
      const next = idx >= 0 ? [...arr.slice(0, idx), payload, ...arr.slice(idx + 1)] : [payload, ...arr];
      await saveJSON(PRODUCTS_FILE, next);
      return true;
    } catch {
      return false;
    }
  }

  function learnFromManufacturingTime(payload) {
    try {
      const type = (() => {
        const cat = payload.category || payload.type || "Custom";
        return ["Mug", "Planter", "Bowl", "Plate", "Vase", "Custom"].includes(cat) ? cat : "Custom";
      })();

      const batchSize = payload.batchSize || payload.quantity || 1;
      if (!batchSize || !payload.time) return;

      const perPiece = (minutesTotal) => (batchSize > 0 ? Number(minutesTotal || 0) / batchSize : 0);

      const sumStaffMinutes = (stageName) => {
        const stage = payload.time?.[stageName];
        if (!stage || !Array.isArray(stage.staffAssignments)) return 0;
        return stage.staffAssignments.reduce((acc, s) => acc + Number(s.minutes || 0), 0);
      };

      const makingMinutes = sumStaffMinutes("Making");
      const glazingMinutes = sumStaffMinutes("Glazing");
      const finishingMinutes = sumStaffMinutes("Finishing");

      if (makingMinutes > 0) updateWithActuals(type, "throw", perPiece(makingMinutes));
      if (glazingMinutes > 0) updateWithActuals(type, "glaze", perPiece(glazingMinutes));
      if (finishingMinutes > 0) {
        if (payload.hasHandle) updateWithActuals(type, "attach_handle", perPiece(finishingMinutes));
        else updateWithActuals(type, "trim", perPiece(finishingMinutes));
      }
    } catch (e) {
      console.error("[AdvancedCalculator] learnFromManufacturingTime failed", e);
    }
  }

  return (
    <div className="w-full h-full bg-zinc-900 text-white overflow-auto -m-4" ref={exportRef}>
      {/* Header */}
      <div className="px-6 pt-6 flex justify-between items-center mb-6 sticky top-0 bg-zinc-900 z-50 border-b border-zinc-700 pb-6">
          <div className="flex items-center gap-4">
          <img src={logo} alt="Logo" className="w-10 h-10" />
          <h1 className="text-2xl font-bold">Advanced Product Calculator</h1>
        </div>
        <button
          onClick={() => {
            onBack?.();
            play("tap");
            buzz("light");
          }}
          className="bg-orange-600 hover:bg-orange-700 px-4 py-2 rounded"
        >
          ← Back to Dashboard
        </button>
      </div>

      {/* Content wrapper */}
      <div className="px-6 pb-6">
        {/* Product Photo */}
        <section className="mb-6">
          <h2 className="text-lg font-semibold border-b border-zinc-700 pb-1">Product Photo</h2>

        <div className="mt-3 grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
          {/* Drop zone / preview */}
          <div className="lg:col-span-1">
            <div
              ref={dropRef}
              role="button"
              tabIndex={0}
              onClick={pickFile}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && pickFile()}
              className={`relative rounded overflow-hidden flex items-center justify-center border ${
                isDragging ? "border-blue-400" : "border-zinc-700"
              }`}
              style={{
                minHeight: 220,
                background: product.imageUrl
                  ? "transparent"
                  : "repeating-linear-gradient(45deg, #2a2a2a 0 10px, #242424 10px 20px)",
                outline: isDragging ? "2px dashed rgba(59,130,246,0.6)" : "none",
              }}
              title="Click or drop an image here (or paste)"
            >
              {product.imageUrl ? (
                <img
                  src={product.imageUrl}
                  alt={product.name || "Product"}
                  className="h-full w-full object-cover"
                  draggable={false}
                />
              ) : (
                <div className="p-4 text-center text-zinc-300">
                  <div className="text-sm mb-1">Drop image here</div>
                  <div className="text-xs opacity-80">or click to upload / paste</div>
                </div>
              )}
              {isDragging && <div className="absolute inset-0 bg-blue-500/10 pointer-events-none" />}
            </div>
          </div>

          {/* Controls */}
          <div className="lg:col-span-1">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/heic,image/heif,image/svg+xml"
              className="hidden"
              onChange={onFileChange}
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={onFileChange}
            />

            <p className="text-sm text-zinc-300 mb-2">
              Drag & drop, paste, or upload a photo. We’ll <strong>compress it to ≤ 2MB</strong> for you.
            </p>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={pickFile}
                className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded text-white"
              >
                Upload
              </button>
              <button
                type="button"
                onClick={pickCamera}
                className="bg-emerald-600 hover:bg-emerald-700 px-4 py-2 rounded text-white"
              >
                Camera
              </button>
              {product.imageUrl && (
                <button
                  type="button"
                  onClick={removePhoto}
                  className="bg-zinc-700 hover:bg-zinc-800 px-4 py-2 rounded"
                >
                  Remove
                </button>
              )}
            </div>

            {photoError && <p className="mt-2 text-xs text-red-300">{photoError}</p>}
            <p className="mt-2 text-xs text-zinc-400">
              PNG/JPG/WEBP/HEIC/HEIF/SVG supported. Max dimensions 2000×2000px (scaled if larger).
            </p>
          </div>

          {/* Helper Panel - positioned to the right on larger screens */}
          <div className="lg:col-span-1 hidden lg:block">
            <CalculatorHelperPanel />
          </div>
        </div>
      </section>

      {/* 1) Product Info */}
      <section className="mb-6">
  <h2 className="text-lg font-semibold border-b border-zinc-700 pb-1">Product Info</h2>
        <div className="grid grid-cols-1 md-grid-cols-4 md:grid-cols-4 gap-4 mt-2">
          <input
            placeholder="Name"
            value={product.name || ""}
            onChange={(e) => handleField("name", e.target.value)}
            onBlur={() => play("tap")}
            className="w-full px-3 py-2 mb-3 border rounded bg-zinc-800 text-white"
          />
          <select
            className="w-full px-3 py-2 mb-3 border rounded bg-zinc-800 text-white"
            value={product.category || ""}
            onChange={(e) => {
              handleField("category", e.target.value);
              play("tap");
            }}
          >
            <option value="">-- Category --</option>
            {defaultCategories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <select
            className="w-full px-3 py-2 mb-3 border rounded bg-zinc-800 text-white"
            value={product.method || ""}
            onChange={(e) => {
              handleField("method", e.target.value);
              play("tap");
            }}
          >
            <option value="">-- Method --</option>
            {["3D Printing", "Slip Casting", "Wheel Throwing"].map((m) => (
              <option key={m}>{m}</option>
            ))}
          </select>
          <input
            type="number"
            placeholder="Quantity"
            value={product.quantity || ""}
            onChange={(e) => handleField("quantity", Number(e.target.value))}
            onBlur={() => play("tap")}
            className="w-full px-3 py-2 mb-3 border rounded bg-zinc-800 text-white"
          />
        </div>
      </section>

      {/* 2) Parametric 3D Builder */}
      <section className="mb-6">
        <h2 className="text-lg font-semibold border-b border-zinc-700 pb-1">Parametric 3D Builder</h2>
        <ParametricBuilder onMetricsChange={handleParametricMetrics} />
      </section>

      {/* 3) Material & Glaze Estimation */}
      <section className="mb-6 bg-gray-800 p-4 rounded space-y-2">
        <h2 className="text-lg font-semibold border-b border-zinc-700 pb-1">Material & Glaze Estimation</h2>
        <p>Shell Volume: {activeShellVolCm3.toFixed(2)} cm³</p>
        <p>External Surface Area: {activeOuterAreaCm2.toFixed(2)} cm²</p>
        <p>Internal Surface Area: {activeInnerAreaCm2.toFixed(2)} cm²</p>
        <label className="block text-sm">Clay Type</label>
        <select
          className="w-full p-2 bg-gray-700 rounded text-white"
          value={clayType}
          onChange={(e) => {
            const v = e.target.value;
            setClayType(v);
            updateProduct({ ...product, clayType: v });
            play("tap");
          }}
        >
          {defaultClays.map((c) => (
            <option key={c.name} value={c.name}>
              {c.name}
            </option>
          ))}
        </select>
        <p className="text-right">Clay Weight: {clayWeight} g</p>
      </section>

      {/* 4) Material Picker */}
      <section className="mb-6">
  <h2 className="text-lg font-semibold">Material</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
          <select
            value={product.materialId || ""}
            onChange={(e) => {
              handleField("materialId", e.target.value);
              play("tap");
            }}
            className="input input-bordered bg-zinc-900 text-white"
          >
            <option value="">-- Choose Clay --</option>
            {allMaterials
              .filter((m) => m.category?.toLowerCase() === "clay")
              .map((m) => {
                const perG =
                  (parseFloat(m.purchaseCost) || 0) / (parseFloat(m.conversionAmount) || 1);
                return (
                  <option key={m.id} value={m.id}>
                    {m.name} (${perG.toFixed(4)}/g)
                  </option>
                );
              })}
          </select>
          <div className="p-2 bg-gray-800 rounded text-right">
            <div className="text-sm text-gray-400">Unit Cost: ${materialUnitCost.toFixed(4)}/g</div>
            <div className="text-lg font-bold">Material Cost: ${materialCost.toFixed(2)}</div>
          </div>
        </div>
      </section>

      {/* 5) Glazing Layers */}
      <section className="mb-6">
        <h2 className="text-lg font-semibold border-b border-zinc-700 pb-1">Glazing Layers</h2>
        <div className="space-y-4">
          {(product.glazing || []).map((layer, i) => {
            const mat = customGlaze.find((m) => m.name === layer.type);
            const uc = mat
              ? (parseFloat(mat.purchaseCost) || 0) / (parseFloat(mat.conversionAmount) || 1)
              : 0;
            const useInnerArea = parametricMetrics ? activeInnerAreaCm2 : innerAreaCm2;
            const useOuterArea = parametricMetrics ? activeOuterAreaCm2 : outerAreaCm2;
            const area =
              layer.scope === "Internal"
                ? useInnerArea
                : layer.scope === "External"
                ? useOuterArea
                : useInnerArea + useOuterArea;
            const thickCm = (Number(layer.thickness) || 0) / 10;
            const volCoat = area * thickCm;
            const totalV =
              volCoat * (Number(layer.coats) || 1) * (Number(layer.coverage ?? 100) / 100);
            const cost = totalV * uc;

            return (
              <div key={i} className="p-4 bg-gray-800 rounded">
                <div className="grid grid-cols-1 md:grid-cols-7 gap-4 items-end">
                  <select
                    className="input input-bordered bg-zinc-900 text-white"
                    value={layer.type}
                    onChange={(e) => {
                      const up = [...(product.glazing || [])];
                      up[i].type = e.target.value;
                      updateProduct({ ...product, glazing: up });
                      play("tap");
                    }}
                  >
                    {glazeOptions.length > 0 ? (
                      glazeOptions.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))
                    ) : (
                      <option disabled value="">
                        Add glaze first
                      </option>
                    )}
                  </select>

                  <div className="flex flex-col gap-1">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="1"
                      className="w-full accent-orange-500 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                      value={layer.coverage ?? 100}
                      onChange={(e) => {
                        const up = [...(product.glazing || [])];
                        up[i].coverage = Number(e.target.value);
                        updateProduct({ ...product, glazing: up });
                      }}
                      onBlur={() => play("tap")}
                      title="Adjust coverage percentage"
                    />
                    <span className="text-xs text-gray-300 font-medium text-center">Coverage: {layer.coverage ?? 100}%</span>
                  </div>

                  <select
                    className="input input-bordered bg-zinc-900 text-white"
                    value={layer.scope}
                    onChange={(e) => {
                      const up = [...(product.glazing || [])];
                      up[i].scope = e.target.value;
                      updateProduct({ ...product, glazing: up });
                      play("tap");
                    }}
                  >
                    {["Internal", "External", "Both"].map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>

                  <select
                    className="input input-bordered bg-zinc-900 text-white"
                    value={layer.method}
                    onChange={(e) => {
                      const m = e.target.value;
                      const up = [...(product.glazing || [])];
                      up[i].method = m;
                      up[i].thickness = defaultMethodThickness[m];
                      updateProduct({ ...product, glazing: up });
                      play("tap");
                    }}
                  >
                    {Object.keys(defaultMethodThickness).map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>

                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="input input-bordered text-black"
                    placeholder="Thickness"
                    value={layer.thickness ?? 0}
                    onChange={(e) => {
                      const up = [...(product.glazing || [])];
                      up[i].thickness = Number(e.target.value);
                      updateProduct({ ...product, glazing: up });
                    }}
                    onBlur={() => play("tap")}
                  />
                  <input
                    type="number"
                    min="1"
                    step="1"
                    className="input input-bordered text-black"
                    placeholder="Coats"
                    value={layer.coats ?? 1}
                    onChange={(e) => {
                      const up = [...(product.glazing || [])];
                      up[i].coats = Number(e.target.value);
                      updateProduct({ ...product, glazing: up });
                    }}
                    onBlur={() => play("tap")}
                  />
                  <button
                    className="bg-red-600 hover:bg-red-700 px-3 py-1 rounded text-white"
                    onClick={() => {
                      const up = [...(product.glazing || [])];
                      up.splice(i, 1);
                      updateProduct({ ...product, glazing: up });
                      play("stop");
                      buzz("light");
                    }}
                  >
                    Remove
                  </button>
                </div>

                <div className="mt-2 text-xs text-gray-200">
                  Type | Coverage % | Scope | Method | Thickness (mm) | Coats
                </div>

                <div className="mt-3 text-right text-sm text-gray-200">
                  Cost: ${cost.toFixed(2)}
                </div>
              </div>
            );
          })}
          <button
            className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded text-white w-full"
            onClick={() => {
              updateProduct({
                ...product,
                glazing: [
                  ...(product.glazing || []),
                  {
                    type: glazeOptions[0] || "",
                    coverage: 100,
                    scope: "External",
                    method: "Dip",
                    thickness: defaultMethodThickness.Dip,
                    coats: 1,
                  },
                ],
              });
              play("start");
              buzz("light");
            }}
          >
            Add Glaze Layer
          </button>
        </div>
      </section>

      {/* 6) Accessories */}
      <section className="mb-6">
  <h2 className="text-lg font-semibold border-b border-zinc-700 pb-1">Accessories</h2>
        <p className="text-sm text-zinc-400 mt-2 mb-3">
          Add items that go with your piece (handles, lids, stamps, add-ons, etc.)
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {(product.accessories || []).map((a, i) => (
            <>
              <input
                placeholder="e.g., Wooden handle"
                value={a.name}
                onChange={(e) => {
                  const up = [...(product.accessories || [])];
                  up[i].name = e.target.value;
                  updateProduct({ ...product, accessories: up });
                }}
                onBlur={() => play("tap")}
                className="input input-bordered"
                title="Name of the accessory (e.g., Handle, Lid, Stamp)"
              />
              <input
                type="number"
                placeholder="e.g., 2"
                value={a.qty || ''}
                onChange={(e) => {
                  const up = [...(product.accessories || [])];
                  up[i].qty = Number(e.target.value);
                  updateProduct({ ...product, accessories: up });
                }}
                onBlur={() => play("tap")}
                className="input input-bordered"
                title="Quantity needed per piece"
              />
              <input
                placeholder="e.g., each, set, pair"
                value={a.unit || ''}
                onChange={(e) => {
                  const up = [...(product.accessories || [])];
                  up[i].unit = e.target.value;
                  updateProduct({ ...product, accessories: up });
                }}
                onBlur={() => play("tap")}
                className="input input-bordered"
                title="Unit of measurement"
              />
              <input
                type="number"
                placeholder="e.g., 3.50"
                value={a.cost || ''}
                onChange={(e) => {
                  const up = [...(product.accessories || [])];
                  up[i].cost = Number(e.target.value);
                  updateProduct({ ...product, accessories: up });
                }}
                onBlur={() => play("tap")}
                className="input input-bordered"
                title="Cost per unit"
                step="0.01"
              />
            </>
          ))}
          <button
            onClick={() => {
              updateProduct({
                ...product,
                accessories: [
                  ...(product.accessories || []),
                  { name: "", qty: 0, unit: "", cost: 0 },
                ],
              });
              play("start");
              buzz("light");
            }}
            className="md:col-span-4 bg-green-600 hover:bg-green-700 px-4 py-2 rounded text-white"
          >
            + Add Accessory
          </button>
        </div>
      </section>

      {/* 7) Manufacturing Time */}
      <section className="mb-6">
  <h2 className="text-lg font-semibold border-b border-zinc-700 pb-1">Manufacturing Time</h2>

        <div className="mb-4">
          <label className="block mb-1 text-white">
            Batch Size (number of items made in this batch)
          </label>
          <input
            type="number"
            min="1"
            value={product.batchSize || 1}
            onChange={(e) => handleField("batchSize", Math.max(1, Number(e.target.value)))}
            onBlur={() => play("tap")}
            className="input input-bordered w-full bg-zinc-800 text-white placeholder-gray-400"
          />
        </div>

        {manufactureEstimate && (
          <div className="mb-4 border border-zinc-700 rounded p-3 bg-black/20">
            <div style={{ marginBottom: 6 }}>
              <strong style={{ fontSize: 14 }}>AI TIME ESTIMATE</strong>
              <div
                style={{
                  fontSize: 12,
                  opacity: 0.65,
                  marginTop: 4,
                  lineHeight: 1.35,
                }}
              >
                Mudo calculates these times using your product dimensions, clay
                category, making method and glazing layers. Estimates adapt over
                time as you record real making and glazing minutes in the
                Manufacturing Time section below.
              </div>
            </div>
            <ul className="text-sm space-y-1">
              {manufactureEstimate.steps.map((step) => (
                <li key={step.key}>
                  {step.key === "throw"
                    ? "Throwing"
                    : step.key === "trim"
                    ? "Trimming"
                    : step.key === "attach_handle"
                    ? "Handles"
                    : step.key === "glaze"
                    ? "Glazing"
                    : step.key}
                  {": "}
                  {step.minutes} min per piece
                </li>
              ))}
            </ul>
            <div className="text-sm mt-2">
              Total making time: <strong>{manufactureEstimate.totalMinutes} min per piece</strong>
            </div>
            {(manufactureEstimate.firing?.bisqueHours || manufactureEstimate.firing?.glazeHours) && (
              <div className="text-xs mt-1 opacity-80">
                Firing hint: bisque ~{manufactureEstimate.firing?.bisqueHours ?? "?"} h, glaze ~
                {manufactureEstimate.firing?.glazeHours ?? "?"} h (full load).
              </div>
            )}
          </div>
        )}

        {[
          "Preparation",
          "Making",
          "Drying",
          "Bisque Firing",
          "Glazing",
          "Glaze Firing",
          "Finishing",
        ].map((stage) => {
          const stageData = product.time?.[stage] || {};
          const assignments = stageData.staffAssignments || [];
          const chargeType = stageData.chargeType || "In-house";

          return (
            <div key={stage} className="border border-zinc-700 rounded p-4 mb-4">
              <h3 className="font-semibold mb-2 text-white">{stage}</h3>

              <div className="mb-4">
                <label className="block text-sm text-white mb-1">Charge Type</label>
                <select
                  value={chargeType}
                  onChange={(e) => {
                    const upTime = { ...(product.time || {}) };
                    upTime[stage] = { ...upTime[stage], chargeType: e.target.value };
                    updateProduct({ ...product, time: upTime });
                    play("tap");
                  }}
                  className="input input-bordered w-full bg-zinc-800 text-white"
                >
                  <option>In-house</option>
                  <option>Outsourced</option>
                  <option>Non-billable</option>
                  <option>Skip</option>
                </select>
              </div>

              {chargeType === "Outsourced" && (
                <div className="mb-4">
                  <label className="block text-sm text-white mb-1">Manual External Cost ($)</label>
                  <input
                    type="number"
                    min="0"
                    value={stageData.manualCost || ""}
                    onChange={(e) => {
                      const upTime = { ...(product.time || {}) };
                      upTime[stage] = { ...upTime[stage], manualCost: Number(e.target.value) };
                      updateProduct({ ...product, time: upTime });
                    }}
                    onBlur={() => play("tap")}
                    className="input input-bordered w-full bg-zinc-800 text-white placeholder-gray-400"
                  />
                </div>
              )}

              {(chargeType === "In-house" || chargeType === "Non-billable") && (
                <>
                  {assignments.map((a, idx) => (
                    <div
                      key={idx}
                      className="grid grid-cols-1 md:grid-cols-4 gap-4 items-start mb-2"
                    >
                      <select
                        value={a.staffId}
                        onChange={(e) => {
                          const sel = (staffList || []).find((s) => s.id === e.target.value);
                          const upTime = { ...(product.time || {}) };
                          const arr = [...assignments];
                          arr[idx] = {
                            ...arr[idx],
                            staffId: e.target.value,
                            rate: sel?.hourlyRate || 0,
                          };
                          upTime[stage] = { ...upTime[stage], staffAssignments: arr };
                          updateProduct({ ...product, time: upTime });
                          play("tap");
                        }}
                        className="input input-bordered bg-zinc-800 text-white"
                      >
                        <option value="" disabled>
                          -- Select Staff --
                        </option>
                        {(staffList || []).map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name} ({s.role})
                          </option>
                        ))}
                      </select>

                      <div className="flex flex-col">
                        <input
                          type="number"
                          min="0"
                          placeholder="Minutes"
                          value={a.minutes || ""}
                          onChange={(e) => {
                            const val = Number(e.target.value);
                            const upTime = { ...(product.time || {}) };
                            const arr = [...assignments];
                            arr[idx] = { ...arr[idx], minutes: val };
                            upTime[stage] = { ...upTime[stage], staffAssignments: arr };
                            updateProduct({ ...product, time: upTime });
                          }}
                          onBlur={() => play("tap")}
                          className="input input-bordered bg-zinc-800 text-white"
                        />
                        <p className="mt-1 text-xs italic text-gray-400">Time in minutes</p>
                      </div>

                      <div className="flex flex-col">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Rate/hr"
                          value={a.rate || ""}
                          onChange={(e) => {
                            const val = Number(e.target.value);
                            const upTime = { ...(product.time || {}) };
                            const arr = [...assignments];
                            arr[idx] = { ...arr[idx], rate: val };
                            upTime[stage] = { ...upTime[stage], staffAssignments: arr };
                            updateProduct({ ...product, time: upTime });
                          }}
                          onBlur={() => play("tap")}
                          className="input input-bordered bg-zinc-800 text-white"
                        />
                        <p className="mt-1 text-xs italic text-gray-400">Hourly rate</p>
                      </div>

                      <button
                        onClick={() => {
                          const upTime = { ...(product.time || {}) };
                          const arr = [...assignments];
                          arr.splice(idx, 1);
                          upTime[stage] = { ...upTime[stage], staffAssignments: arr };
                          updateProduct({ ...product, time: upTime });
                          play("stop");
                          buzz("light");
                        }}
                        className="self-start bg-red-600 hover:bg-red-700 px-3 py-1 rounded text-white"
                      >
                        Remove
                      </button>
                    </div>
                  ))}

                  <button
                    onClick={() => {
                      const upTime = { ...(product.time || {}) };
                      const arr = [...assignments, { staffId: "", minutes: 0, rate: 0 }];
                      upTime[stage] = { ...upTime[stage], staffAssignments: arr };
                      updateProduct({ ...product, time: upTime });
                      play("start");
                      buzz("light");
                    }}
                    className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded text-white"
                  >
                    + Add Staff
                  </button>
                </>
              )}
            </div>
          );
        })}
      </section>

      {/* 8) Packaging & Shipping — UPDATED */}
      <section className="mb-6">
        <h2 className="text-lg font-semibold border-b border-zinc-700 pb-1">
          Packaging & Shipping
        </h2>

        {/* Library-driven items */}
        <div className="mt-3 space-y-3">
          <div className="text-sm text-zinc-300">
            Select items saved in your <strong>Materials</strong> with category <em>Packaging</em>.
          </div>

          {(product.packaging?.items || []).map((it, idx) => {
            const mat = packagingMats.find((m) => String(m.id) === String(it.materialId));
            const unit = packagingUnitCost(mat);
            const qty = Number(it.qty) || 0;
            const rowTotal = unit * qty;

            return (
              <div key={idx} className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end p-3 bg-gray-800 rounded">
                <select
                  className="input input-bordered bg-zinc-900 text-white"
                  value={it.materialId || ""}
                  onChange={(e) => {
                    const items = [...(product.packaging?.items || [])];
                    items[idx] = { ...items[idx], materialId: e.target.value };
                    handleField("packaging", { ...(product.packaging || {}), items });
                    play("tap");
                  }}
                >
                  <option value="">-- Select Packaging Item --</option>
                  {packagingMats.map((m) => {
                    const perUnit = packagingUnitCost(m);
                    return (
                      <option key={m.id} value={m.id}>
                        {m.name}
                        {m.category ? ` (${m.category})` : ""} — ${perUnit.toFixed(4)} / unit
                      </option>
                    );
                  })}
                </select>

                <input
                  type="number"
                  min="0"
                  className="input input-bordered bg-white text-black"
                  placeholder="e.g., 1"
                  value={it.qty ?? ""}
                  onChange={(e) => {
                    const items = [...(product.packaging?.items || [])];
                    items[idx] = { ...items[idx], qty: Number(e.target.value) };
                    handleField("packaging", { ...(product.packaging || {}), items });
                  }}
                  onBlur={() => play("tap")}
                  title="Quantity of this packaging item needed per piece"
                />

                <div className="p-2 bg-white rounded text-sm text-black font-medium">
                  Unit: ${unit.toFixed(4)}
                </div>

                <div className="p-2 bg-white rounded text-sm text-black font-medium">
                  Subtotal: ${rowTotal.toFixed(2)}
                </div>

                <button
                  className="bg-red-600 hover:bg-red-700 px-3 py-2 rounded text-white"
                  onClick={() => {
                    const items = [...(product.packaging?.items || [])];
                    items.splice(idx, 1);
                    handleField("packaging", { ...(product.packaging || {}), items });
                    play("stop");
                    buzz("light");
                  }}
                >
                  Remove
                </button>
              </div>
            );
          })}

          <button
            className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded text-white"
            onClick={() => {
              const items = [...(product.packaging?.items || []), { materialId: "", qty: 0 }];
              handleField("packaging", { ...(product.packaging || {}), items });
              play("start");
              buzz("light");
            }}
          >
            + Add Packaging Item
          </button>
        </div>

        {/* Rules summary */}
        <div className="mt-4 p-3 bg-zinc-800 rounded border border-zinc-700">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Rules Applied</h3>
            <div className="text-sm text-zinc-300">
              Per-unit (from rules): <strong>${ruleUsage.perUnitCost.toFixed(4)}</strong> •
              Per-batch: <strong>${ruleUsage.perBatchCost.toFixed(2)}</strong> •
              Total (for qty): <strong>${ruleUsage.totalCost.toFixed(2)}</strong>
            </div>
          </div>

          <div className="mt-2 grid gap-2">
            {ruleUsage.breakdown?.length ? (
              ruleUsage.breakdown.map((u, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between text-sm bg-zinc-900 rounded px-2 py-1"
                >
                  <div className="truncate">
                    <span className="font-medium">{u.item?.name || "Item"}</span>{" "}
                    <span className="text-zinc-400">
                      • {u.rule?.appliesAs} • {u.qtyTotal} {u.item?.uom || "unit"}
                    </span>
                  </div>
                  <div>
                    ${Number(u.costTotal || 0).toFixed(2)}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-sm text-zinc-400">No rule usage for this product/quantity.</div>
            )}
          </div>
        </div>

        {/* Legacy/extra + shipping & manual total */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
          <input
            type="number"
            placeholder="Extra Packaging Cost (misc.)"
            value={product.packaging?.cost || ""}
            onChange={(e) =>
              handleField("packaging", {
                ...(product.packaging || {}),
                cost: Number(e.target.value),
              })
            }
            onBlur={() => play("tap")}
            className="input input-bordered"
          />
          <input
            type="number"
            placeholder="Shipping Cost"
            value={product.packaging?.shipping || ""}
            onChange={(e) =>
              handleField("packaging", {
                ...(product.packaging || {}),
                shipping: Number(e.target.value),
              })
            }
            onBlur={() => play("tap")}
            className="input input-bordered"
          />
          <div className="p-2 bg-gray-800 rounded flex items-center justify-between">
            <span className="text-sm text-zinc-300">Manual Items Total</span>
            <strong>${packagingItemsCost.toFixed(2)}</strong>
          </div>
        </div>
      </section>

      {/* NEW: Process → Planner (Auto) */}
      <section className="mb-6">
        <h2 className="text-lg font-semibold border-b border-zinc-700 pb-1">
          Process → Planner (Auto)
        </h2>
        <div className="mt-3 grid gap-3 p-4 bg-zinc-800 rounded border border-zinc-700">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!process.autoPlan}
              onChange={(e) => {
                setProcess({ autoPlan: e.target.checked });
                play("tap");
              }}
            />
            <span>Auto-plan this product when saving</span>
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm block mb-1">Start date/time</label>
              <input
                type="datetime-local"
                value={process.startDate ? new Date(process.startDate).toISOString().slice(0,16) : ""}
                onChange={(e) =>
                  setProcess({ startDate: e.target.value ? new Date(e.target.value).toISOString() : undefined })
                }
                disabled={!process.autoPlan}
                className={`w-full px-3 py-2 rounded bg-zinc-900 border border-zinc-700 ${
                  !process.autoPlan ? "opacity-60 cursor-not-allowed" : ""
                }`}
              />
            </div>

            <div>
              <label className="text-sm block mb-1">Drying (days)</label>
              <input
                type="number"
                min="0"
                step="0.5"
                value={process.dryingDays ?? 2}
                onChange={(e) => setProcess({ dryingDays: Number(e.target.value) })}
                disabled={!process.autoPlan}
                className={`w-full px-3 py-2 rounded bg-zinc-900 border border-zinc-700 ${
                  !process.autoPlan ? "opacity-60 cursor-not-allowed" : ""
                }`}
              />
            </div>

            <div>
              <label className="text-sm block mb-1">Prep/Buffer (hours)</label>
              <input
                type="number"
                min="0"
                step="1"
                value={process.bufferHours ?? 6}
                onChange={(e) => setProcess({ bufferHours: Number(e.target.value) })}
                disabled={!process.autoPlan}
                className={`w-full px-3 py-2 rounded bg-zinc-900 border border-zinc-700 ${
                  !process.autoPlan ? "opacity-60 cursor-not-allowed" : ""
                }`}
              />
            </div>

            <div>
              <label className="text-sm block mb-1">Bisque firing (hours)</label>
              <input
                type="number"
                min="0"
                step="1"
                value={process.bisqueHours ?? 14}
                onChange={(e) => setProcess({ bisqueHours: Number(e.target.value) })}
                disabled={!process.autoPlan}
                className={`w-full px-3 py-2 rounded bg-zinc-900 border border-zinc-700 ${
                  !process.autoPlan ? "opacity-60 cursor-not-allowed" : ""
                }`}
              />
            </div>

            {/* Bisque cool down */}
            <div>
              <label className="text-sm block mb-1">Bisque firing Cool Down (hours)</label>
              <input
                type="number"
                min="0"
                step="1"
                value={process.bisqueCooldownHours ?? 24}
                onChange={(e) => setProcess({ bisqueCooldownHours: Number(e.target.value) })}
                disabled={!process.autoPlan}
                className={`w-full px-3 py-2 rounded bg-zinc-900 border border-zinc-700 ${!process.autoPlan ? "opacity-60 cursor-not-allowed" : ""}`}
              />
            </div>

            <div>
              <label className="text-sm block mb-1">Glaze firing (hours)</label>
              <input
                type="number"
                min="0"
                step="1"
                value={process.glazeHours ?? 12}
                onChange={(e) => setProcess({ glazeHours: Number(e.target.value) })}
                disabled={!process.autoPlan}
                className={`w-full px-3 py-2 rounded bg-zinc-900 border border-zinc-700 ${
                  !process.autoPlan ? "opacity-60 cursor-not-allowed" : ""
                }`}
              />
            </div>

            {/* Glaze cool down */}
            <div>
              <label className="text-sm block mb-1">Glaze firing Cool Down (hours)</label>
              <input
                type="number"
                min="0"
                step="1"
                value={process.glazeCooldownHours ?? 24}
                onChange={(e) => setProcess({ glazeCooldownHours: Number(e.target.value) })}
                disabled={!process.autoPlan}
                className={`w-full px-3 py-2 rounded bg-zinc-900 border border-zinc-700 ${
                  !process.autoPlan ? "opacity-60 cursor-not-allowed" : ""
                }`}
              />
            </div>
          </div>

          <p className="text-xs text-zinc-400">
            Tip: Auto-planning respects your app setting <code>autoPlanFromCalculator</code>. If that’s
            enabled (default) and this product has <em>Auto-plan</em> checked, tasks will be created in your Planner on save.
          </p>
        </div>
      </section>

      {/* 9) Overheads & Profit */}
      <section className="mb-6">
        <h2 className="text-lg font-semibold border-b border-zinc-700 pb-1">Overheads & Profit</h2>

        <div className="mt-3 p-3 bg-zinc-800 rounded border border-zinc-700">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={useMarketingCtx}
              onChange={(e) => {
                setUseMarketingCtx(e.target.checked);
                handleField("overheads", {
                  ...(product.overheads || {}),
                  useMarketingCtx: e.target.checked,
                });
                play("tap");
              }}
            />
            <span>
              Use <strong>Marketing Context</strong> total in overheads
              {useMarketingCtx ? " (recommended)" : ""}
            </span>
          </label>
          <div className="mt-2 text-xs text-zinc-300">
            Current Marketing (source: {useMarketingCtx ? "Context" : "Manual"}):{" "}
            <strong>
              ${Number(useMarketingCtx ? marketingMonthlyTotal : product?.overheads?.marketing || 0).toFixed(2)}
            </strong>{" "}
            / mo
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
          <input
            type="number"
            placeholder="Salary / mo"
            value={product.overheads?.salary || ""}
            onChange={(e) =>
              handleField("overheads", {
                ...(product.overheads || {}),
                salary: Number(e.target.value),
              })
            }
            onBlur={() => play("tap")}
            className="input input-bordered"
          />

          <input
            type="number"
            placeholder="Marketing / mo (manual)"
            value={product.overheads?.marketing || ""}
            disabled={useMarketingCtx}
            onChange={(e) =>
              handleField("overheads", {
                ...(product.overheads || {}),
                marketing: Number(e.target.value),
              })
            }
            onBlur={() => play("tap")}
            className={`input input-bordered ${useMarketingCtx ? "opacity-60 cursor-not-allowed" : ""}`}
            title={useMarketingCtx ? "Using Marketing Context total" : "Manual marketing per month"}
          />

          <input
            type="number"
            placeholder={`Units / mo (default ${settings?.productionOutput || 0})`}
            value={product.overheads?.output || ""}
            onChange={(e) =>
              handleField("overheads", {
                ...(product.overheads || {}),
                output: Number(e.target.value),
              })
            }
            onBlur={() => play("tap")}
            className="input input-bordered"
          />
        </div>

        <div className="mt-4">
          <label className="block mb-1">Profit Margin: {product.profit || 0}%</label>
          <input
            type="range"
            min="0"
            max="100"
            value={product.profit || 0}
            onChange={(e) => handleField("profit", Number(e.target.value))}
            onMouseUp={() => play("tap")}
            className="w-full"
          />
        </div>
      </section>

      {/* 10) Cost Breakdown & Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-zinc-800 rounded p-4">
          <h3 className="mb-2 text-center font-semibold">Cost Breakdown</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="name"
                outerRadius={100}
                label={({ value }) => `$${formatMoney(value)}`}
              >
                {chartData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v, name) => [`$${formatMoney(v)}`, name]} />
              <Legend
                formatter={(value, entry) =>
                  `${value} — $${formatMoney(entry?.payload?.value)}`
                }
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-zinc-800 rounded p-4">
          <h3 className="mb-2 font-semibold">Cost Summary</h3>
          <p>Material: <strong>${materialCost.toFixed(2)}</strong></p>
          <p>Glaze: <strong>${glazeCost.toFixed(2)}</strong></p>
          <p>Staff: <strong>${staffCost.toFixed(2)}</strong></p>
          <p>Utilities: <strong>${utilityCost.toFixed(2)}</strong></p>
          <p>Accessories: <strong>${accessoriesCost.toFixed(2)}</strong></p>
          <p>Packaging: <strong>${packagingCost.toFixed(2)}</strong></p>
          <p>Overheads: <strong>${overheadCost.toFixed(2)}</strong></p>
          <hr className="my-2 border-zinc-700" />
          <p className="font-bold text-lg">
            Total Cost: <strong>${totalCost.toFixed(2)}</strong>
          </p>
          <p className="font-bold text-xl text-green-400 mt-4">
            Final Price (+{product.profit || 0}%):{" "}
            <strong>
              {(totalCost * (1 + (product.profit || 0) / 100)).toFixed(2)}
            </strong>
          </p>
          <div className="mt-4 flex gap-2">
            <button
              onClick={exportPDF}
              className="bg-orange-600 hover:bg-orange-700 px-4 py-2 rounded"
            >
              PDF
            </button>
            <button
              onClick={exportExcel}
              className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded"
            >
              Excel
            </button>
          </div>
        </div>

        <PricingCoachPanel coach={pricingCoach} currency={settings?.currencySymbol || "$"} />
      </div>

      <div className="grid gap-4 md:grid-cols-2 mt-4">
        <ScheduleCoachPanel
          schedule={scheduleCoach}
          dueDate={product?.dueDate}
          batchLabel={batchLabel}
          batchId={batchId}
        />
      </div>

      {/* Save / Cancel / Save as Template / Save Draft to Workspace */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={async () => {
            const payload = {
              ...product,
              segments,
              wallThickness: wallThk,
              baseThickness: baseThk,
              batchSize: product.batchSize || 1,
              updatedAt: Date.now(),
            };

            learnFromManufacturingTime(payload);

            // Save to parent (e.g., state/LS)
            onSaveProduct?.(payload);

            // Mirror to workspace if available
            const mirrored = await saveDraftToWorkspace(payload);
            if (mirrored) {
              play("success"); buzz("light");
            }

            // Auto-plan to Planner if enabled
            const autoPlanEnabled = settings?.autoPlanFromCalculator !== false;
            if (autoPlanEnabled && payload?.process?.autoPlan) {
              try {
                const tasks = makePlannerTasksFromProduct(payload);
                planner?.upsertEvents?.(tasks);
                play("success"); buzz("success");
              } catch (e) {
                console.error("[Planner] upsertEvents failed", e);
                play("error"); buzz("error");
              }
            } else {
              play("success"); buzz("success");
            }
          }}
          className="bg-green-600 hover:bg-green-700 px-6 py-2 rounded text-white"
        >
          Save
        </button>

        <button
          onClick={async () => {
            const draft = {
              ...product,
              segments,
              wallThickness: wallThk,
              baseThickness: baseThk,
              batchSize: product.batchSize || 1,
              updatedAt: Date.now(),
            };
            const ok = await saveDraftToWorkspace(draft);
            if (ok) { play("success"); buzz("light"); } else { play("error"); buzz("error"); }
          }}
          className="bg-zinc-700 hover:bg-zinc-800 px-6 py-2 rounded text-white"
          title="Mirror this product to your selected data folder (products.json)"
        >
          Save Draft to Workspace
        </button>

        <button
          onClick={() => {
            onBack?.();
            play("tap");
            buzz("light");
          }}
          className="bg-gray-600 hover:bg-gray-700 px-6 py-2 rounded text-white"
        >
          Cancel
        </button>

        <button
          onClick={() => {
            try {
              const finalPrice = Number(
                (totalCost * (1 + (product.profit || 0) / 100)).toFixed(2)
              );
              const template = {
                id: Date.now().toString(),
                name: product.name || "Untitled",
                category: product.category || "",
                method: product.method || "",
                quantity: product.quantity || 1,
                finalPrice,
                createdAt: Date.now(),
                payload: {
                  ...product,
                  segments,
                  wallThickness: wallThk,
                  baseThickness: baseThk,
                  batchSize: product.batchSize || 1,
                  updatedAt: Date.now(),
                },
              };

              const existing = JSON.parse(localStorage.getItem("mudoTemplates") || "[]");
              const next = Array.isArray(existing) ? [...existing, template] : [template];
              localStorage.setItem("mudoTemplates", JSON.stringify(next));
              window.dispatchEvent(new Event("mudo:templates-updated"));
              play("success"); buzz("success");
            } catch (e) {
              console.error("[Save template] failed:", e);
              play("error"); buzz("error");
            }
          }}
          className="bg-amber-500 hover:bg-amber-600 px-6 py-2 rounded text-black"
        >
          Save as Template
        </button>
      </div>
      </div>    </div>
  );
}